import { ArrowUpRightIcon } from "lucide-react";
import { Anchor } from "~/components/Anchor";
import { SectionHeader } from "~/components/SectionHeader";
import { NetworkUserEvents } from "~/modules/analytics/events/networks";

// // // //

function formatNewsDate(date: Date): string {
    const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
    ];

    const month = months[date.getMonth()];
    const year = date.getFullYear();

    return `${month} ${year}`;
}

// // // //

interface NewsItem {
    website: string;
    name: string;
    publishedAt: string;
}

function NewsCard(props: { newsItem: NewsItem }) {
    const { newsItem } = props;

    return (
        <Anchor
            href={newsItem.website || ""}
            openInNewTab
            analyticsLabel={
                NetworkUserEvents.ClickNetworkServiceProviderWebsite
            }
            className="block"
        >
            <div className="px-4 py-2 hover:bg-gray-50 transition-colors group">
                <div className="flex items-center justify-between min-w-0">
                    <h3 className="text-sm text-foreground truncate">
                        {newsItem.name}
                    </h3>
                    <div className="flex items-center gap-2 ml-4">
                        <p className="text-xs text-secondary-foreground whitespace-nowrap tabular-nums">
                            {formatNewsDate(new Date(newsItem.publishedAt))}
                        </p>
                        <ArrowUpRightIcon className="w-4 h-4 text-secondary-foreground" />
                    </div>
                </div>
            </div>
        </Anchor>
    );
}

// // // //

export function BroadridgeNews() {
    const results: NewsItem[] = [
        {
            name: "Broadridge's Tokenization Capability Enables Societe Generale's First U.S. Digital Bond Issuance",
            website:
                "https://www.broadridge.com/press-release/2025/broadridge-enables-societe-generales-first-us-digital-bond",
            publishedAt: "2025-11-01",
        },
        {
            name: "$280 Billion in Average Daily Processed Trade Volumes on Broadridge Distributed Ledger Repo Platform",
            website:
                "https://www.broadridge.com/press-release/2025/billions-in-average-daily-processed-trade-volumes-on-broadridge-dlt-repo-platform",
            publishedAt: "2025-09-01",
        },
        {
            name: "Return on Innovation: Transforming Liquidity Management with Intraday Repo",
            website:
                "https://www.broadridge.com/white-paper/capital-markets/return-on-innovation-intraday-repo-has-arrived-on-scale",
            publishedAt: "2025-04-01",
        },
        {
            name: "Broadridge Collaborates with Fnality to Enable Real-Time Settlement for Intraday Repo Transactions",
            website:
                "https://www.broadridge.com/press-release/2025/broadridge-collaborates-with-fnality",
            publishedAt: "2025-04-09",
        },
        {
            name: "Digital assets and smart contracts are transforming one of the world's largest markets",
            website:
                "https://www.broadridge.com/article/capital-markets/digital-assets-and-smart-contracts-are-transforming-markets",
            publishedAt: "2025-02-18",
        },
        {
            name: "2025 Crypto Golden Age: AI & Blockchain Unleashing Innovation",
            website:
                "https://www.forbes.com/sites/roomykhan/2024/12/04/2025-crypto-golden-age-ai--blockchain-unleashing-innovation/",
            publishedAt: "2024-12-04",
        },
        {
            name: "DLT in the Real World 2024 Report",
            website:
                "https://www.broadridge.com/article/capital-markets/dlt-in-the-real-world-2024",
            publishedAt: "2024-07-01",
        },
        {
            name: "From ripples to waves: The transformational power of tokenizing assets",
            website:
                "https://www.mckinsey.com/industries/financial-services/our-insights/from-ripples-to-waves-the-transformational-power-of-tokenizing-assets",
            publishedAt: "2024-06-20",
        },
        {
            name: "HSBC to Go Live on Broadridge Distributed Ledger Sponsored Repo Solution",
            website:
                "https://www.broadridge.com/press-release/2023/hsbc-to-go-live-on-broadridge-distributed-ledger-sponsored-repo-solution",
            publishedAt: "2023-11-15",
        },
        {
            name: "UBS Goes Live on Broadridge's New Distributed Ledger Sponsored Repo Solution",
            website:
                "https://www.broadridge.com/press-release/2023/ubs-goes-live-on-broadridges-new-distributed-ledger-sponsored-repo-solution",
            publishedAt: "2023-10-01",
        },
        {
            name: "DRW Executes Intraday Repo Transactions",
            website:
                "https://www.broadridge.com/press-release/2023/drw-executes-intraday-repo-transactions",
            publishedAt: "2023-05-23",
        },
        {
            name: "Societe Generale to Join Broadridge's DLR Platform",
            website:
                "https://www.broadridge.com/press-release/2022/societe-generale-to-join-broadridges-dlr-platform",
            publishedAt: "2022-06-29",
        },
        {
            name: "The Next-Gen Tool for Central Clearing: DLT - Enabled Sponsored Repo",
            website:
                "https://www.broadridge.com/article/capital-markets/the-next-gen-tool-for-central-clearing-dlt-enabled-sponsored-repo",
            publishedAt: "2024-02-01",
        },
        {
            name: "Distributed Ledger Repo Enters Its Next Phase",
            website:
                "https://www.broadridge.com/article/capital-markets/securities-finance-times-distributed-ledger-repo-enters-its-next-phase",
            publishedAt: "2023-04-01",
        },
    ];

    const sortedResults = results.sort(
        (a, b) =>
            new Date(b.publishedAt).getTime() -
            new Date(a.publishedAt).getTime(),
    );

    return (
        <>
            <SectionHeader label="Recent News" />

            <div className="divide-y mt-3 bg-card rounded-lg shadow-xs border overflow-hidden">
                {sortedResults.map((sp) => (
                    <NewsCard key={sp.website} newsItem={sp} />
                ))}
            </div>
        </>
    );
}
